<!-- eslint-disable vue/no-v-html -->
<script setup>
import { ref, reactive } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { useVuelidate } from "@vuelidate/core";
import { required, email, integer } from "@vuelidate/validators";
import { provideApolloClient } from "@vue/apollo-composable";

const props = defineProps({
  eventId: {
    type: Number,
    required: true,
  },
  hasFullCapacity: {
    type: Boolean,
    required: true,
  },
  isOpen: { type: Boolean, default: false },
});

const emit = defineEmits(["close"]);

function closeModal() {
  emit("close");
}

const isSubmitting = ref(false);
const submitted = ref(false);

const state = reactive({
  action: "reservation",
  firstname: "",
  lastname: "",
  email: "",
  company: "",
  event_id: props.eventId,
});

const rules = {
  firstname: { required },
  lastname: { required },
  email: { required, email },
  company: { required },
};

const v$ = ref(useVuelidate(rules, state));

const query = gql`
  mutation makeReservation(
    $email: String!
    $company: String!
    $firstname: String!
    $lastname: String!
    $event_id: String!
  ) {
    eventForm(
      input: {
        email: $email
        company: $company
        firstname: $firstname
        lastname: $lastname
        event_id: $event_id
      }
    ) {
      message
      event
      currently_registered
    }
  }
`;

const { mutate: eventForm } = useMutation(query);

const submitForm = async () => {
  isSubmitting.value = true;

  try {
    const isFormCorrect = await v$.value.$validate();

    if (!isFormCorrect) {
      isSubmitting.value = false;
      return;
    }

    const variables = {
      email: state.email,
      company: state.company,
      firstname: state.firstname,
      lastname: state.lastname,
      event_id: state.event_id.toString(),
    };

    const response = await eventForm(variables);

    isSubmitting.value = false;
    submitted.value = true;
  } catch (error) {
    console.log("Login error:", error);
    isSubmitting.value = false;
  }
};
</script>

<template>
  <TransitionRoot as="template" :show="isOpen">
    <Dialog
      as="div"
      class="relative z-50"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 w-screen overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
                class="relative overflow-hidden bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl"
              >
              <div class="modal-bg">
                <div class="modal-form">
                  <div
                    class="modal-form-grid mx-auto flex flex-shrink-0 items-start gap-40 justify-center sm:mx-0 pt-16 px-4"
                  >


      <div v-if="hasFullCapacity">
        <h3 class="h1 mb-5 text-center" v-html="$t('eventform.capacity')"></h3>
      </div>
      <div v-else-if="!hasFullCapacity && !submitted" class="max-w-[590px] mx-auto">
        <h3 class="h1 mb-5 text-center">{{ $t("eventform.title") }}</h3>
        <div class="mb-2.5" :class="{ error: v$.firstname.$errors.length }">
          <input
            v-model="state.firstname"
            type="text"
            :placeholder="$t('eventform.name')"
            class="w-full block border-b-[1px] border-[#3a3a3a] placeholder-black !bg-transparent py-3 pl-6 pr-3 focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm"
            :class="{
              'border-[#DA0000] text-[#DA0000]': v$.firstname.$errors.length,
            }"
          />
          <p
            v-if="v$.firstname.$errors.length"
            class="text-xs pl-6 pt-1 text-[#DA0000]"
          >
            {{ $t("eventform.name.warning") }}
          </p>
        </div>
        <div class="mb-2.5" :class="{ error: v$.lastname.$errors.length }">
          <input
            v-model="state.lastname"
            type="text"
            :placeholder="$t('eventform.surname')"
            class="w-full block border-b-[1px] border-[#3a3a3a] placeholder-black !bg-transparent py-3 pl-6 pr-3 focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm"
            :class="{
              'border-[#DA0000] text-[#DA0000]': v$.lastname.$errors.length,
            }"
          />
          <p
            v-if="v$.lastname.$errors.length"
            class="text-xs pl-6 pt-1 text-[#DA0000]"
          >
            {{ $t("eventform.surname.warning") }}
          </p>
        </div>
        <div class="mb-2.5" :class="{ error: v$.email.$errors.length }">
          <input
            v-model="state.email"
            type="text"
            :placeholder="$t('eventform.email')"
            class="w-full block border-b-[1px] border-[#3a3a3a] placeholder-black !bg-transparent py-3 pl-6 pr-3 focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm"
            :class="{ 'border-[#DA0000] text-[#DA0000]': v$.email.$errors.length }"
          />
          <p
            v-if="v$.email.$errors.length"
            class="text-xs pl-6 pt-1 text-[#DA0000]"
          >
            {{ $t("eventform.email.warning") }}
          </p>
        </div>
        <div class="mb-2.5" :class="{ error: v$.company.$errors.length }">
          <input
            v-model="state.company"
            type="text"
            :placeholder="$t('eventform.company')"
            class="w-full block border-b-[1px] border-[#3a3a3a] placeholder-black !bg-transparent py-3 pl-6 pr-3 focus:outline-none focus:ring-transparent focus:ring-1 sm:text-sm"
            :class="{
              'border-[#DA0000] text-[#DA0000]': v$.company.$errors.length,
            }"
          />
          <p
            v-if="v$.company.$errors.length"
            class="text-xs pl-6 pt-1 text-[#DA0000]"
          >
            {{ $t("eventform.company.warning") }}
          </p>
        </div>
        <div class="text-center">
          <button
            :disabled="isSubmitting"
            class="bg-transparent hover:bg-[#1A1A1A] uppercase border border-[#1A1A1A] rounded-full hover:text-white mt-4 mb-4 px-4 py-2"
            @click="submitForm"
          >
            {{ $t("eventform.confirm") }}
          </button>
        </div>
      </div>
      <div v-else-if="submitted">
        <h3 class="h1 mb-5 text-center" v-html="$t('eventform.thx')"></h3>
      </div>



      </div>

								</div>
              </div>
              <div
                class="absolute top-0 right-0 text-xl p-4 cursor-pointer"
                @click="closeModal"
              >
                ✖
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
